import BaseModal from '@/components/BaseModal';
import { InfoIcon } from '@/components/Icons';
import UpgradeTextWithTouchPoint from '@/components/UpgradeTextWithTouchPoint';
import { NumberTimelineIconStages, Plan, TimelineIconStages, Touchpoint } from '@/constants/enum';
import DefaultIconTab from '@/pages/Settings/Components/Appearance/components/DefaultIconTab';
import settingsSlice, { dataSettingsSelector } from '@/redux/slice/settings.slice';
import { IParamsApi } from '@/types/apis/params';
import { Button, InlineGrid } from '@shopify/polaris';
import { ImageIcon, UploadIcon } from '@shopify/polaris-icons';
import { memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import UploadIconTab from './UploadIconTab';

interface IProps {
  active: boolean;
  onClose: () => void;
  stage?: TimelineIconStages;
}

function ModalChangeIcon({ active, onClose, stage }: IProps) {
  const dispatch = useDispatch();
  const dataSettings = useSelector(dataSettingsSelector);
  const [tab, setTab] = useState<0 | 1>(stage && dataSettings[stage] && dataSettings[stage]?.isCustom ? 1 : 0);
  const [selectedIcon, setSelectedIcon] = useState<IParamsApi.IDataSettingTimelineIcon | undefined>(
    stage ? dataSettings[stage] : undefined,
  );
  const [hasErrorFile, setHasErrorFile] = useState(false);

  useEffect(() => {
    setSelectedIcon(stage ? dataSettings[stage] : undefined);
    if (stage && dataSettings[stage] && dataSettings[stage]?.isCustom) {
      setTab(1);
    } else setTab(0);
  }, [dataSettings, stage]);

  const handleChange = (key: keyof typeof dataSettings) => (value: IParamsApi.IDataSettingTimelineIcon) => {
    dispatch(
      settingsSlice.actions.handleDataSettings({
        ...dataSettings,
        [key]: value,
      }),
    );
  };

  const handleSave = () => {
    if (tab === 0) {
      const data = { ...selectedIcon };
      if (stage && dataSettings[stage] && dataSettings[stage]?.iconUrl) {
        data.iconUrl = dataSettings[stage]?.iconUrl as string;
        data.isCustom = false;
      }
      stage && selectedIcon && handleChange(stage)(data);
    } else {
      if (hasErrorFile || !selectedIcon) {
        onClose();
        return;
      }
      stage && selectedIcon && handleChange(stage)({ ...selectedIcon, isCustom: true });
    }
    onClose();
  };

  return (
    <BaseModal
      size="large"
      open={active}
      onClose={onClose}
      title={`Change icon stage ${NumberTimelineIconStages[stage as TimelineIconStages]}`}
      primaryAction={{
        content: 'Save',
        onAction: handleSave,
        disabled: dataSettings.plan === Plan.FREE,
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: onClose,
        },
      ]}
    >
      {dataSettings.plan === Plan.FREE ? (
        <div className="mt-8 mb-16">
          <UpgradeTextWithTouchPoint touchPoint={Touchpoint.AppearanceChangeIcon} text="Standard or above" />
        </div>
      ) : null}

      <InlineGrid gap="400" columns={{ xs: 2 }}>
        <div className={tab === 0 ? 'timeline-actived-tab' : ''}>
          <Button onClick={() => setTab(0)} fullWidth variant="tertiary" icon={ImageIcon}>
            Icons
          </Button>
        </div>
        <div className={tab === 1 ? 'timeline-actived-tab' : ''}>
          <Button onClick={() => setTab(1)} fullWidth variant="tertiary" icon={UploadIcon}>
            Upload
          </Button>
        </div>
      </InlineGrid>
      {tab === 0 ? (
        <div className="d-flex mt-8 mb-8 align-center">
          <span className="mr-4" style={{ height: 18 }}>
            <InfoIcon width={20} height={20} />
          </span>
          You can’t change the color of multi-color or animated icons.
        </div>
      ) : null}
      <div className={dataSettings.plan === Plan.FREE ? 'disable-content' : ''}>
        {tab === 0 ? (
          <DefaultIconTab setSelectedIcon={setSelectedIcon} selectedIcon={selectedIcon} />
        ) : (
          <UploadIconTab setSelectedIcon={setSelectedIcon} selectedIcon={selectedIcon} setHasErrorFile={setHasErrorFile} />
        )}
      </div>
    </BaseModal>
  );
}

export default memo(ModalChangeIcon);
